import React from "react";
import { frame, radius, tokens } from "../../../theme";

/**
 * The frame, as functions.
 *
 * The book's frame section states nine numbers and then says "nothing in this
 * book changes those three numbers". They were, in fact, changed everywhere:
 * the rail was 220 in one place and 80 collapsed in another, the gutter was
 * 1.25rem on the left and 2rem on the right, and a "card" was 10px, 12px or
 * 0.5px-bordered depending on which screen you were on.
 *
 * The numbers now live once in `theme.frame`. This module turns them into the
 * handful of shapes the shell needs, as pure functions, so the arithmetic can
 * be tested without mounting the app.
 */

/** Media query strings, so no component re-types a breakpoint. */
export const COMPACT_QUERY = `@media (max-width: ${frame.compactBreakpoint - 1}px)`;
export const PHONE_QUERY = `@media (max-width: ${frame.phoneBreakpoint - 1}px)`;

/**
 * How wide the rail is right now.
 *
 * Below 1024 the rail is the 72px icon rail whatever the user last chose — the
 * book is explicit that it collapses rather than becoming an overlay drawer, so
 * there is no third state where a 240px rail sits over a narrow page.
 */
export const railWidth = (collapsed, viewportWidth) => {
  const forced = typeof viewportWidth === "number" && viewportWidth < frame.compactBreakpoint;
  return collapsed || forced ? frame.sidebarCollapsedWidth : frame.sidebarWidth;
};

/** Is the viewport in the narrow band — icon rail, 20px gutter, 24px cards? */
export const isCompact = (viewportWidth) =>
  typeof viewportWidth === "number" && viewportWidth < frame.compactBreakpoint;

/**
 * Is the viewport narrow enough that the rail must be the icon rail?
 *
 * The CSS collapses the rail's width on its own, but the rows inside it are
 * rendered from a JS flag — so without this the labels stayed mounted and were
 * simply clipped by a 72px panel. Defaults to false so the first paint on a
 * wide screen is the wide layout rather than a flash of the rail.
 */
export const useCompactRail = () => {
  const [compact, setCompact] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${frame.compactBreakpoint - 1}px)`);
    const apply = () => setCompact(mq.matches);
    apply();
    // addListener is the pre-Safari-14 spelling; both are kept because this
    // runs in whatever the customer's browser is.
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  return compact;
};

/**
 * The one scroll container on the page.
 *
 * Fixed to the viewport below the top bar and right of the rail, which is what
 * makes it the *only* thing that scrolls: the bar and the rail are outside it,
 * so they cannot move, and the document itself has nothing left to scroll.
 */
export const scrollColumnSx = (collapsed) => ({
  position: "fixed",
  top: `${frame.topBarHeight}px`,
  right: 0,
  bottom: 0,
  left: `${railWidth(collapsed)}px`,
  overflowY: "auto",
  // Never sideways. A wide table scrolls inside its own card instead.
  overflowX: "hidden",
  background: tokens.surface,
  transition: `left 180ms cubic-bezier(0.2, 0, 0.2, 1)`,
  [COMPACT_QUERY]: { left: `${frame.sidebarCollapsedWidth}px` },
});

/**
 * The content column inside that scroller: fluid to 1240, then centred, so a
 * 27-inch monitor gets whitespace rather than a 2000px table row.
 */
export const contentColumnSx = () => ({
  width: "100%",
  maxWidth: `${frame.contentMaxWidth}px`,
  marginInline: "auto",
  padding: `${frame.gutter}px`,
  display: "flex",
  flexDirection: "column",
  // "Never a card that ends short of the column edge" — every band takes the
  // full width of the column, so a card cannot shrink to its own content.
  alignItems: "stretch",
  // The gap between bands is the same 20 as between cards within a band.
  gap: `${frame.cardGap}px`,
  [COMPACT_QUERY]: { padding: `${frame.gutterNarrow}px` },
});

/**
 * A card: a band on the page. 14px radius, 32px pad, hairline border, card
 * ground. Padding drops to 24 below 1024 and nothing else changes.
 */
export const cardSx = (overrides = {}) => ({
  background: tokens.card,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${radius.card}px`,
  padding: `${frame.cardPadding}px`,
  [COMPACT_QUERY]: { padding: `${frame.cardPaddingNarrow}px` },
  ...overrides,
});

/**
 * A panel: a box inside a card. 10px radius, and it never takes the card's own
 * padding — a panel that pads like its parent reads as a second card.
 */
export const panelSx = (overrides = {}) => ({
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${radius.panel}px`,
  ...overrides,
});

/**
 * A band of cards sitting side by side.
 *
 * The rule the book states is negative: "never a card that ends short of the
 * column edge". Equal columns with the standard gap is the only arrangement
 * that satisfies it without every screen doing its own arithmetic, and below
 * 1024 they stack in reading order — which is source order, so the screen must
 * put the card it is about first.
 */
export const bandRowSx = (columns) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap: `${frame.cardGap}px`,
  alignItems: "stretch",
  [COMPACT_QUERY]: { gridTemplateColumns: "minmax(0, 1fr)" },
});

/**
 * The counter band: one row, four to seven, never two.
 *
 * Anything outside 4-7 is a screen that has not decided what it is about, so
 * the count is clamped and the caller is told in development rather than being
 * silently given a second row.
 */
export const COUNTERS_MIN = 4;
export const COUNTERS_MAX = 7;

export const counterColumns = (count) => {
  if (!Number.isFinite(count) || count < 1) return 1;
  return Math.min(Math.max(Math.round(count), 1), COUNTERS_MAX);
};

/**
 * The narrow reflow. Below 1024 a row of counters becomes two rows of three or
 * three of two — never a ragged wrap, and never a counter narrow enough to
 * wrap its own label.
 */
export const counterColumnsCompact = (count) => {
  const n = counterColumns(count);
  if (n <= 2) return n;
  if (n <= 4) return 2;
  return 3;
};

export const counterRowSx = (count) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${counterColumns(count)}, minmax(0, 1fr))`,
  gap: `${frame.cardGap}px`,
  [COMPACT_QUERY]: {
    gridTemplateColumns: `repeat(${counterColumnsCompact(count)}, minmax(0, 1fr))`,
  },
});

/**
 * A wide table's own scroller, with the first column pinned.
 *
 * This is the single exception to "a card never scrolls internally". The pin is
 * done here rather than per screen because a sticky first column needs the
 * header cell and the body cell to agree on both offset and z-index, and every
 * screen that tried it separately got one of the two wrong.
 */
export const tableScrollSx = () => ({
  overflowX: "auto",
  overflowY: "visible",
  "& thead th:first-of-type, & tbody td:first-of-type, & tfoot td:first-of-type": {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: tokens.card,
  },
  "& thead th:first-of-type": { zIndex: 3, background: tokens.surface },
});

export default {
  railWidth,
  useCompactRail,
  isCompact,
  scrollColumnSx,
  contentColumnSx,
  cardSx,
  panelSx,
  bandRowSx,
  counterColumns,
  counterColumnsCompact,
  counterRowSx,
  tableScrollSx,
};
